<template>
  <iconify-icon
    class="zp-icon"
    :icon="iconRef"
    :width="size"
    :height="size"
    :style="colorStyle"
    :aria-hidden="ariaHiddenAttr"
    :aria-label="ariaLabel || null"
    :role="ariaLabel ? 'img' : null"
    v-on="$listeners"
  />
</template>

<script>
// ZpIcon — thin Vue wrapper around the iconify-icon web component. All
// ZetaPilot UI surfaces should use Lucide icons through this component;
// upstream Semaphore screens may still use Vuetify <v-icon>mdi-*</v-icon>
// until migrated.
//
// Default set is Lucide; pass `:set="'tabler'"` to use any other Iconify
// collection. We pre-bundle the Lucide set via addCollection in main.js so
// no network fetch is required at runtime (air-gapped deployments work).
//
// Accessibility:
//   - Decorative (default): aria-hidden="true". Safe for icons that sit
//     inside a v-btn or alongside text that names the action.
//   - Interactive / standalone: pass :aria-label="..." or :ariaLabel="...".
//     Sets role="img" + aria-label and removes aria-hidden so screen
//     readers announce the icon.
//
// Listeners (@click, @mouseenter, @keydown, ...) are forwarded to the
// iconify-icon root via v-on="$listeners". This means consumers can use
// @click (no .native modifier needed) and v-tooltip activators
// (v-on="on") work correctly.

export default {
  name: 'ZpIcon',

  inheritAttrs: false,

  props: {
    name: {
      type: String,
      default: '',
    },
    set: {
      type: String,
      default: 'lucide',
    },
    size: {
      type: [String, Number],
      default: 18,
    },
    color: {
      type: String,
      default: null,
    },
    // Explicit accessible label for interactive icons. When set, the
    // wrapper exposes role="img" + aria-label and drops aria-hidden.
    ariaLabel: {
      type: String,
      default: null,
    },
  },

  computed: {
    iconRef() {
      // Tolerate empty / null / undefined names — render a placeholder
      // glyph instead of crashing in this.name.includes(':').
      if (!this.name) {
        return `${this.set}:square`;
      }
      // Allow passing fully qualified names ("lucide:server-cog") as well
      // as bare ones ("server-cog") with the configured set as a prefix.
      return this.name.includes(':') ? this.name : `${this.set}:${this.name}`;
    },

    colorStyle() {
      return this.color ? { color: this.color } : null;
    },

    ariaHiddenAttr() {
      return this.ariaLabel ? null : 'true';
    },
  },
};
</script>

<style>
.zp-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: -0.125em;
  line-height: 1;
  /* Reserve a 1em box so the host doesn't collapse during async icon
     fetch — keeps click targets alive and prevents layout shift. */
  min-width: 1em;
  min-height: 1em;
}
</style>
